import type { CampaignRow, CampaignStatus, Channel } from "../Campaigns";

/**
 * Campaign results. Sample numbers until the campaigns API reports real
 * delivery and conversion data; seeded by campaign ID so a campaign always
 * shows the same figures. Replace `campaignPerformance` with an API call.
 */

export type ChannelResult = {
  channel: Channel;
  sent: number;
  delivered: number;
  opened: number | null;
  clicked: number;
  converted: number;
  revenue: number;
};

export type DayResult = { date: string; converted: number; revenue: number };

export type CampaignPerformance = {
  reached: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  converted: number;
  revenue: number;
  pointsIssued: number;
  pointsCost: number;
  controlSize: number;
  controlConverted: number;
  conversionRate: number;
  controlRate: number;
  liftPts: number;
  incrementalRevenue: number;
  roi: number;
  costPerConversion: number;
  avgOrderValue: number;
  byChannel: ChannelResult[];
  byDay: DayResult[];
};

export function hasResults(status: CampaignStatus) {
  return status === "Ongoing" || status === "Ended";
}

function parseCompact(value: string): number {
  const match = value.trim().replace(/,/g, "").match(/^([\d.]+)\s*([KM])?/i);
  if (!match) return 0;
  const scale = match[2]?.toUpperCase() === "M" ? 1e6 : match[2]?.toUpperCase() === "K" ? 1e3 : 1;
  return Math.round(Number(match[1]) * scale);
}

function seededRandom(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const CHANNEL_RATES: Record<
  Channel,
  { weight: number; delivered: number; opened: number | null; clicked: number; converted: number }
> = {
  WhatsApp: { weight: 1.3, delivered: 0.97, opened: 0.78, clicked: 0.19, converted: 0.24 },
  Email: { weight: 1, delivered: 0.95, opened: 0.31, clicked: 0.14, converted: 0.18 },
  SMS: { weight: 0.8, delivered: 0.93, opened: null, clicked: 0.05, converted: 0.2 },
  "In-app": { weight: 0.7, delivered: 1, opened: 0.62, clicked: 0.22, converted: 0.21 },
  Web: { weight: 0.5, delivered: 1, opened: 0.45, clicked: 0.07, converted: 0.15 },
};

function parseRowDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function campaignPerformance(row: CampaignRow, costPerPoint: number): CampaignPerformance {
  const rand = seededRandom(row.id + row.code);
  const jitter = (spread = 0.15) => 1 + (rand() * 2 - 1) * spread;

  const reached = parseCompact(row.membersReached) || 5000;
  const sentTotal = parseCompact(row.messages) || Math.round(reached * 1.3);
  const channels = row.channels.length ? row.channels : (["Email"] as Channel[]);
  const weightSum = channels.reduce((sum, c) => sum + CHANNEL_RATES[c].weight, 0);
  const avgOrderValue = Math.round(1400 * jitter(0.3));

  const byChannel: ChannelResult[] = channels.map((channel) => {
    const rate = CHANNEL_RATES[channel];
    const sent = Math.round((sentTotal * rate.weight) / weightSum);
    const delivered = Math.round(sent * Math.min(1, rate.delivered * jitter(0.02)));
    const opened = rate.opened == null ? null : Math.round(delivered * rate.opened * jitter());
    const clicked = Math.round((opened ?? delivered) * rate.clicked * jitter());
    const converted = Math.round(clicked * rate.converted * jitter());
    return {
      channel,
      sent,
      delivered,
      opened,
      clicked,
      converted,
      revenue: Math.round(converted * avgOrderValue * jitter(0.1)),
    };
  });

  const sum = (pick: (c: ChannelResult) => number) => byChannel.reduce((acc, c) => acc + pick(c), 0);
  const sent = sum((c) => c.sent);
  const delivered = sum((c) => c.delivered);
  const opened = sum((c) => c.opened ?? 0);
  const clicked = sum((c) => c.clicked);
  const converted = sum((c) => c.converted);
  const revenue = sum((c) => c.revenue);

  const pointsIssued = parseCompact(row.pointsIssued);
  const pointsCost = pointsIssued * costPerPoint;
  const conversionRate = reached ? converted / reached : 0;
  const controlSize = Math.round(reached / 9); // 10% held out of the full audience
  const controlRate = Math.max(0.004, conversionRate * (0.45 + rand() * 0.25));
  const controlConverted = Math.round(controlSize * controlRate);
  const liftPts = (conversionRate - controlRate) * 100;
  const incrementalRevenue = Math.max(0, Math.round((conversionRate - controlRate) * reached * avgOrderValue));

  const start = parseRowDate(row.startDate) ?? new Date(Date.now() - 30 * 864e5);
  const endRaw = parseRowDate(row.endDate) ?? new Date();
  const end = endRaw.getTime() > Date.now() ? new Date() : endRaw;
  const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / 864e5));
  const points = Math.min(30, totalDays);
  const step = totalDays / points;
  const weights = Array.from({ length: points }, (_, i) => Math.exp(-i / (points / 2.5)) * jitter(0.35) + 0.15);
  const weightTotal = weights.reduce((a, b) => a + b, 0);
  const byDay: DayResult[] = weights.map((w, i) => {
    const date = new Date(start.getTime() + Math.round(i * step) * 864e5);
    const share = w / weightTotal;
    return {
      date: date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      converted: Math.round(converted * share),
      revenue: Math.round(revenue * share),
    };
  });

  return {
    reached,
    sent,
    delivered,
    opened,
    clicked,
    converted,
    revenue,
    pointsIssued,
    pointsCost,
    controlSize,
    controlConverted,
    conversionRate,
    controlRate,
    liftPts,
    incrementalRevenue,
    roi: pointsCost ? revenue / pointsCost : 0,
    costPerConversion: converted ? pointsCost / converted : 0,
    avgOrderValue,
    byChannel,
    byDay,
  };
}
